import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Heart, Loader2, Sparkles, CheckCircle2, XCircle, ArrowLeft, Trophy } from "lucide-react";
import { toast } from "sonner";

export function DonatePage() {
  const { user } = useAuth();
  const [tiers, setTiers] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(null);

  useEffect(() => {
    api.get("/donations/tiers").then((r) => setTiers(r.data));
    api.get("/donations/leaderboard").then((r) => setLeaderboard(r.data));
  }, []);

  const donate = async (lookupKey) => {
    if (!user) return;
    setLoading(lookupKey);
    try {
      const { data } = await api.post("/payments/checkout", {
        lookup_key: lookupKey,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not start checkout");
      setLoading(null);
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <Link to="/dashboard" data-testid="donate-back" className="text-sm text-[color:var(--bee-muted)] hover:text-[color:var(--bee-text)] inline-flex items-center gap-1 mb-6">
        <ArrowLeft size={14} /> Back
      </Link>

      <div className="grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2">
          <div className="mb-8 animate-fade-up">
            <div className="inline-flex items-center gap-2 bee-chip mb-4">
              <Heart size={12} className="text-[color:var(--bee-primary-hover)] fill-current" />
              <span className="tracking-wide">Keep the hive buzzing</span>
            </div>
            <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight">Support FocusBee</h1>
            <p className="text-[color:var(--bee-muted)] mt-3 max-w-xl leading-relaxed">
              FocusBee is free for every student, forever. If BeeAI has helped you cram, or you've made a study friend here — chip in whatever you like. Every donation keeps the servers, LLMs and study rooms running.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            {tiers.map((t) => (
              <button
                key={t.lookup_key}
                onClick={() => donate(t.lookup_key)}
                disabled={loading !== null}
                data-testid={`donate-tier-${t.lookup_key}`}
                className="bee-card p-6 text-left group hover:border-[color:var(--bee-primary)] disabled:opacity-60"
              >
                <div className="flex items-baseline justify-between">
                  <div className="font-display text-4xl font-bold">${(t.amount / 100).toFixed(0)}</div>
                  {loading === t.lookup_key && <Loader2 className="animate-spin text-[color:var(--bee-muted)]" size={18} />}
                </div>
                <div className="mt-2 text-sm font-semibold">{t.label}</div>
                <div className="mt-3 text-xs text-[color:var(--bee-muted)]">One-time · secure via Stripe</div>
              </button>
            ))}
          </div>

          <div className="mt-8 bee-card p-5 flex items-start gap-3 bg-[color:var(--bee-surface-alt)]">
            <Sparkles size={18} className="text-[color:var(--bee-primary-hover)] mt-0.5 flex-shrink-0" />
            <div className="text-sm">
              <div className="font-semibold">Every donor earns a badge.</div>
              <div className="text-[color:var(--bee-muted)] mt-0.5">
                Any donation unlocks the <b>💛 Contributor</b> badge on your profile. Donate $25+ to become a <b>👑 Queen Donor</b>.
              </div>
            </div>
          </div>
        </div>

        <aside>
          <div className="bee-card p-5">
            <h3 className="font-display font-semibold mb-3 flex items-center gap-2"><Trophy size={16} /> Top supporters</h3>
            {leaderboard.length === 0 ? (
              <p className="text-sm text-[color:var(--bee-muted)] italic">Be the first supporter of FocusBee 🐝</p>
            ) : (
              <ul className="space-y-2">
                {leaderboard.map((u, i) => (
                  <li key={u.id} className="flex items-center gap-2 text-sm">
                    <span className="w-5 text-xs text-[color:var(--bee-muted)] font-bold">{i + 1}.</span>
                    <div className="w-7 h-7 hex-clip flex-shrink-0" style={{ background: u.avatar_color }} />
                    <span className="flex-1 truncate">{u.name}</span>
                    <span className="text-xs font-bold text-[color:var(--bee-primary-hover)]">${(u.total_donated_cents / 100).toFixed(0)}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <p className="text-[10px] text-[color:var(--bee-muted)] mt-3">Test mode: use card <code className="bg-[color:var(--bee-surface-alt)] px-1">4242 4242 4242 4242</code>, any future date, any CVC.</p>
        </aside>
      </div>
    </div>
  );
}

export function DonateSuccess() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const { refresh } = useAuth();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState({ payment_status: "pending", amount: 0 });
  const [tries, setTries] = useState(0);

  useEffect(() => {
    if (!sessionId) return;
    let cancelled = false;
    let attempts = 0;

    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (cancelled) return;
        setStatus(data);
        attempts += 1;
        setTries(attempts);
        if (data.payment_status === "paid") {
          refresh();
          return;
        }
        if (data.payment_status === "failed" || data.payment_status === "expired") return;
        if (attempts < 8) setTimeout(poll, 2000);
      } catch { /* retry silently */ }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId, refresh]);

  const paid = status.payment_status === "paid";
  const failed = status.payment_status === "failed" || status.payment_status === "expired";
  const pending = !paid && !failed;

  return (
    <div className="max-w-lg mx-auto px-4 py-16 text-center">
      <div className="bee-card p-8">
        {pending && (
          <>
            <Loader2 size={56} className="animate-spin mx-auto text-[color:var(--bee-primary)]" />
            <h1 className="font-display text-2xl font-bold mt-6" data-testid="donate-pending">Confirming your donation...</h1>
            <p className="text-[color:var(--bee-muted)] mt-2 text-sm">This usually takes a few seconds. Attempt {tries}/8.</p>
          </>
        )}
        {paid && (
          <>
            <CheckCircle2 size={56} className="mx-auto text-[color:var(--bee-online)]" />
            <h1 className="font-display text-3xl font-bold mt-6" data-testid="donate-success">Thank you! 🐝</h1>
            <p className="text-[color:var(--bee-muted)] mt-2">
              Your ${(status.amount / 100).toFixed(2)} donation is confirmed. The <b>💛 Contributor</b> badge is now on your profile.
            </p>
            <div className="mt-6 flex gap-2 justify-center">
              <button onClick={() => nav("/profile")} className="bee-btn-primary">See your badge</button>
              <button onClick={() => nav("/dashboard")} className="bee-btn-secondary">Dashboard</button>
            </div>
          </>
        )}
        {failed && (
          <>
            <XCircle size={56} className="mx-auto text-red-500" />
            <h1 className="font-display text-2xl font-bold mt-6" data-testid="donate-failed">Payment didn't go through</h1>
            <p className="text-[color:var(--bee-muted)] mt-2 text-sm">Please try again — no charges were made.</p>
            <button onClick={() => nav("/donate")} className="bee-btn-primary mt-6">Try again</button>
          </>
        )}
      </div>
    </div>
  );
}

export function DonateCancel() {
  return (
    <div className="max-w-lg mx-auto px-4 py-16 text-center">
      <div className="bee-card p-8">
        <XCircle size={56} className="mx-auto text-[color:var(--bee-muted)]" />
        <h1 className="font-display text-2xl font-bold mt-6" data-testid="donate-cancelled">Donation cancelled</h1>
        <p className="text-[color:var(--bee-muted)] mt-2 text-sm">No charges were made. You can come back anytime.</p>
        <Link to="/donate" className="bee-btn-primary mt-6 inline-block">Try again</Link>
      </div>
    </div>
  );
}
